import { Suspense, useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { CameraControls, Sparkles, Environment, Lightformer, SoftShadows } from "@react-three/drei";
import { EffectComposer, Vignette, ToneMapping, BrightnessContrast, HueSaturation } from "@react-three/postprocessing";
import { ToneMappingMode } from "postprocessing";
import { Decor } from "./Decor";
import { Objects } from "./Objects";
import { HOME_VIEW, START_VIEW, viewFor } from "./registry";
import { useContent } from "@/content/ContentContext";
import { getTexture } from "./textures";

const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);

const Rig = ({ entered, activeId, isMobile, resetKey }) => {
  const controls = useThree((s) => s.controls);
  const { sections } = useContent();
  const first = useRef(true);
  const active = sections.find((s) => s.id === activeId);
  useEffect(() => {
    if (!controls) return;
    if (!entered) {
      controls.setLookAt(...START_VIEW.pos, ...START_VIEW.target, false);
      return;
    }
    const view = active ? viewFor(active).focus : HOME_VIEW;
    const k = isMobile ? (activeId ? 1.7 : 1.35) : 1;
    const pos = view.pos.map((v, i) => view.target[i] + (v - view.target[i]) * k);
    controls.smoothTime = first.current ? 1.4 : 0.75;
    first.current = false;
    controls.setLookAt(...pos, ...view.target, true);
    const d = dist(pos, view.target);
    const ox = activeId && !isMobile ? d * 0.26 : 0;
    const oy = activeId && isMobile ? d * 0.2 : 0;
    controls.setFocalOffset(ox, oy, 0, true);
  }, [controls, entered, activeId, isMobile, resetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!controls) return;
    controls.setBoundary(new THREE.Box3(new THREE.Vector3(-3.9, 0.1, -3.9), new THREE.Vector3(4.6, 4.6, 4.8)));
  }, [controls]);

  // Déplacement au clavier : flèches ou ZQSD / WASD
  useEffect(() => {
    if (!controls || !entered) return undefined;
    const keys = { ArrowUp: [0, 1], KeyW: [0, 1], KeyZ: [0, 1], ArrowDown: [0, -1], KeyS: [0, -1], ArrowLeft: [-1, 0], KeyA: [-1, 0], KeyQ: [-1, 0], ArrowRight: [1, 0], KeyD: [1, 0] };
    const onKey = (e) => {
      const k = keys[e.code];
      if (!k || /INPUT|TEXTAREA|SELECT/.test(e.target?.tagName || "")) return;
      e.preventDefault();
      controls.smoothTime = 0.35;
      if (k[1]) controls.forward(k[1] * 0.45, true);
      if (k[0]) controls.truck(k[0] * 0.45, 0, true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [controls, entered]);
  return null;
};

// Plafond : presque invisible vu de l'extérieur, il devient opaque quand on entre dans la pièce
const Ceiling = () => {
  const mats = useRef([]);
  const tex = useMemo(() => getTexture("plaster", [2.5, 2.5]), []);
  useFrame(({ camera }, dt) => {
    const p = camera.position;
    const out = Math.max(p.x - 4.2, p.z - 4.2, p.y - 4.9, -4.2 - p.x, -4.2 - p.z, 0);
    const target = THREE.MathUtils.clamp(1 - out / 2.2, 0.06, 1);
    mats.current.forEach((m) => {
      if (!m) return;
      m.opacity = THREE.MathUtils.damp(m.opacity, target, 4, dt);
      m.depthWrite = m.opacity > 0.9;
    });
  });
  const reg = (i) => (m) => { mats.current[i] = m; };
  return (
    <group position={[0, 5, 0]}>
      <mesh rotation={[Math.PI / 2, 0, 0]} renderOrder={2}>
        <planeGeometry args={[8.4, 8.4]} />
        <meshStandardMaterial ref={reg(0)} color="#E6D8C2" map={tex} emissive="#6B5444" emissiveMap={tex} emissiveIntensity={0.55} roughness={0.9} transparent opacity={0.06} side={THREE.DoubleSide} />
      </mesh>
      {[-3, -1, 1, 3].map((z, i) => (
        <mesh key={z} position={[0.1, -0.11, z]} renderOrder={2}>
          <boxGeometry args={[8.2, 0.2, 0.24]} />
          <meshStandardMaterial ref={reg(i + 1)} color="#7A4E2E" map={getTexture("wood", [4, 1])} emissive="#3A2416" emissiveIntensity={0.6} roughness={0.6} transparent opacity={0.06} />
        </mesh>
      ))}
    </group>
  );
};

// Rayon de soleil couchant qui traverse la fenêtre
const WindowBeam = () => {
  const target = useMemo(() => { const o = new THREE.Object3D(); o.position.set(-0.6, 0, 0.4); return o; }, []);
  return (
    <>
      <primitive object={target} />
      <spotLight
        position={[-3.75, 3.3, -0.6]}
        target={target}
        color="#FF9F5A"
        intensity={26}
        distance={9}
        angle={0.55}
        penumbra={0.7}
        decay={1.6}
        castShadow
        shadow-mapSize={[1024, 1024]}
        shadow-bias={-0.0005}
      />
    </>
  );
};

const Grade = () => (
  <EffectComposer multisampling={4}>
    <HueSaturation saturation={-0.12} />
    <BrightnessContrast brightness={0.02} contrast={0.08} />
    <ToneMapping mode={ToneMappingMode.ACES_FILMIC} />
    <Vignette offset={0.32} darkness={0.62} />
  </EffectComposer>
);

export default function Scene({ ui, isMobile, resetKey }) {
  return (
    <Canvas
      data-testid="room-3d-canvas"
      shadows
      dpr={[1, 1.75]}
      camera={{ position: START_VIEW.pos, fov: isMobile ? 48 : 36, near: 0.1, far: 120 }}
      onPointerMissed={() => {}}
    >
      <color attach="background" args={["#110C10"]} />
      <fog attach="fog" args={["#140E12", 24, 46]} />
      <SoftShadows size={16} samples={10} focus={0.5} />
      <Environment resolution={128} frames={1} environmentIntensity={0.28}>
        <Lightformer form="rect" intensity={3} color="#FF9A55" position={[-6, 2.5, -0.6]} rotation={[0, Math.PI / 2, 0]} scale={[3, 2, 1]} />
        <Lightformer form="rect" intensity={0.7} color="#FFC9A8" position={[0, 6, 0]} rotation={[Math.PI / 2, 0, 0]} scale={[8, 8, 1]} />
        <Lightformer form="rect" intensity={0.8} color="#7A6AA8" position={[6, 2, 6]} rotation={[0, -Math.PI / 4, 0]} scale={[6, 3, 1]} />
      </Environment>
      <ambientLight intensity={0.32} color="#8C7FB0" />
      <hemisphereLight args={["#FFC8A0", "#2A1F36", 0.45]} />
      <directionalLight
        position={[10, 3.4, 5]}
        intensity={1.55}
        color="#FFA066"
        castShadow
        shadow-mapSize={[2048, 2048]}
        shadow-bias={-0.0004}
        shadow-camera-left={-8}
        shadow-camera-right={8}
        shadow-camera-top={8}
        shadow-camera-bottom={-8}
        shadow-camera-far={30}
      />
      <WindowBeam />
      <pointLight position={[-3.2, 2.8, -0.6]} color="#FFA060" intensity={2.5} distance={6} decay={1.6} />
      <pointLight position={[0.5, 4.3, -3.3]} color="#FFB37A" intensity={1.4} distance={5} decay={1.6} />
      <Suspense fallback={null}>
        <Decor />
        <Objects ui={ui} />
        <Ceiling />
        <Sparkles count={70} scale={[7, 3.6, 7]} position={[0, 2.2, 0]} size={2.4} speed={0.22} opacity={0.6} color="#FFB98A" />
      </Suspense>
      <Grade />
      <CameraControls
        makeDefault
        minDistance={0.5}
        maxDistance={20}
        minPolarAngle={0.15}
        maxPolarAngle={1.52}
        minAzimuthAngle={-0.4}
        maxAzimuthAngle={Math.PI / 2 + 0.4}
        dollySpeed={0.6}
        truckSpeed={1.4}
        dollyToCursor
        enabled={ui.entered}
      />
      <Rig entered={ui.entered} activeId={ui.activeId} isMobile={isMobile} resetKey={resetKey} />
    </Canvas>
  );
}
